import { lazy, Suspense, type ReactNode } from "react";
import { useLocation } from "react-router-dom";

import { ImpersonateBar } from "@/components/auth/ImpersonateBar";
import { ChunkErrorBoundary } from "@/components/ChunkErrorBoundary";
import { CommandPaletteProvider } from "@/components/domain/CommandPaletteProvider";
import { CustomerPhonePaletteSearch } from "@/components/customers/CustomerPhonePaletteSearch";
import { useCustomerPaletteActions } from "@/components/customers/useCustomerPaletteActions";
import { useProjectPaletteActions } from "@/components/projects/useProjectPaletteActions";
import { IosInstallBanner } from "@/components/shell/IosInstallBanner";
import { TopNav } from "@/components/shell/TopNav";
import { WhatsNewGate } from "@/components/shell/WhatsNewGate";
import { TooltipProvider } from "@/components/ui/tooltip";
import { useBackgroundSync } from "@/hooks/useBackgroundSync";

// START-1: der Desktop-Waechter laedt nach — er traegt die Texte jeder
// Kartenart und damit den ganzen Meldungs-Apparat, der sonst auf jeder Seite
// (auch der Anmeldung) im Startbuendel mitliefe. Details am Waechter selbst.
const DesktopNotificationWatcher = lazy(() =>
  import("@/hooks/useDesktopNotificationWatcher").then((m) => ({
    default: m.DesktopNotificationWatcher,
  })),
);

export function AppShell({ children }: { children: ReactNode }) {
  return (
    <TooltipProvider delayDuration={200}>
      <CommandPaletteProvider>
        <AppChrome>{children}</AppChrome>
      </CommandPaletteProvider>
    </TooltipProvider>
  );
}

// AppChrome lebt INNERHALB des CommandPaletteProvider — damit Domain-Hooks wie
// useCustomerPaletteActions den Palette-Kontext nutzen koennen.
function AppChrome({ children }: { children: ReactNode }) {
  useCustomerPaletteActions();
  useProjectPaletteActions();
  useBackgroundSync();
  const location = useLocation();
  return (
    <div className="flex min-h-full flex-col">
      {/* NB-1: neue Meldungen als Desktop-Benachrichtigung (zeichnet nichts).
          POL-1-Grenze wie um die Seiten: ein Ladefehler (Deploy, Netz) loest
          den einen geschuetzten Reload aus statt die ganze App zu kippen. */}
      <ChunkErrorBoundary>
        <Suspense fallback={null}>
          <DesktopNotificationWatcher />
        </Suspense>
      </ChunkErrorBoundary>
      {/* SUCHE-1: Kunden per Telefonnummer in der Schnellsuche (zeichnet nichts). */}
      <CustomerPhonePaletteSearch />
      <IosInstallBanner />
      <ImpersonateBar />
      <TopNav />
      {/* NEU-1: das Fenster «Was ist neu» — einmal pro neuer Version, auf
          jeder Seite, nie in Vertretung. Der Dialog ist ein Lazy-Chunk. */}
      <WhatsNewGate />
      {/* KUND-1 (11.08.2026): hier hing der Freigabe-Dialog, den jede
          schreibende Antwort mit einem `customerMail`-Feld aufspringen lassen
          konnte. Er ist weg — das OS fragt nicht mehr von selbst nach einer
          Kundenmail, der PL loest sie in der Projektliste aus. */}
      {/* key auf pathname: Route-Wechsel setzt die page-enter-CSS-Animation neu. */}
      <main key={location.pathname} className="page-enter flex-1">
        {children}
      </main>
    </div>
  );
}
